import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  Calendar, 
  ClipboardCheck, 
  Settings, 
  LogOut,
  PlusCircle,
  BarChart3,
  Clock,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [nextEvent, setNextEvent] = useState(null);
  const [loadingNextEvent, setLoadingNextEvent] = useState(true);

  useEffect(() => {
    fetchNextEvent();
  }, []);

  const fetchNextEvent = async () => {
    try {
      const response = await axios.get('/events');
      const events = response.data;
      
      // Find next event (today or in the future)
      const now = new Date();
      const upcomingEvents = events
        .filter(event => new Date(event.datum) >= now)
        .sort((a, b) => new Date(a.datum) - new Date(b.datum));
      
      if (upcomingEvents.length > 0) {
        setNextEvent(upcomingEvents[0]);
      }
    } catch (error) {
      console.error('Error fetching next event:', error);
    } finally {
      setLoadingNextEvent(false);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const handleNextEvent = () => {
    if (nextEvent) {
      navigate(`/events/${nextEvent.id}`);
    } else {
      toast.info('Kein anstehendes Event gefunden');
    }
  };

  const quickActions = [
    {
      title: 'Events verwalten',
      description: 'Verwalten Sie alle geplanten Events',
      icon: Calendar,
      action: () => navigate('/events'),
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'from-blue-50 to-indigo-50'
    },
    {
      title: 'Tischvorlagen',
      description: 'Verwalten Sie Ihre Tischkonfigurationen',
      icon: Settings,
      action: () => navigate('/tischvorlagen'),
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'from-purple-50 to-indigo-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Navigation Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg shadow-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold font-space-grotesk text-gray-900">
                  VIP Tischplan
                </h1>
                <p className="text-sm text-gray-600">Event-Management Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {user.role === 'admin' ? 'Administrator' : 'Hostess'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all duration-200"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-slide-in">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Willkommen zurueck, {user.email.split('@')[0]}!
          </h2>
          <p className="text-lg text-gray-600">
            Verwalten Sie Ihre VIP-Events und Tischplaene effizient.
          </p>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {quickActions.map((action, index) => {
            const IconComponent = action.icon;
            return (
              <Card 
                key={index}
                className={`card cursor-pointer transform hover:scale-[1.02] transition-all duration-200 bg-gradient-to-br ${action.bgColor} border-0 shadow-lg hover:shadow-xl animate-slide-in`}
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={action.action}
                data-testid={`quick-action-${index}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl shadow-lg`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-lg font-semibold text-gray-900 mt-3">
                    {action.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {action.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Next Event Card */}
        <Card 
          className="card-glass animate-slide-in cursor-pointer transform hover:scale-[1.01] transition-all duration-200 border-l-4 border-l-amber-500"
          style={{ animationDelay: '200ms' }}
          onClick={handleNextEvent}
          data-testid="next-event-card"
        >
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-xl font-semibold text-gray-900">
                <Clock className="w-6 h-6 mr-2 text-amber-600" />
                Nächstes Event
              </CardTitle>
              <ArrowRight className="w-5 h-5 text-amber-600" />
            </div>
          </CardHeader>
          <CardContent>
            {loadingNextEvent ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent mx-auto"></div>
              </div>
            ) : nextEvent ? (
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">{nextEvent.name}</h3>
                <p className="text-sm text-gray-600">
                  {new Date(nextEvent.datum).toLocaleDateString('de-DE', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <div className="pt-2">
                  <Button 
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  >
                    Event öffnen
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600">Kein anstehendes Event gefunden</p>
                <Button 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/events');
                  }}
                  variant="outline"
                  className="mt-4"
                >
                  Neues Event erstellen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;